import * as React from 'react';
import { useCallback, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';
import PhoneNumber from 'awesome-phonenumber';

import { ticketChannelSelector, ticketContactDataSelector, ticketSalesforceObjectIdSelector, ticketSalesforceObjectTypeSelector } from '@/redux/app/selectors/ticket';
import { AccountLink } from '@/lib/navigation';
import { Link } from '@/lib/navigation/links/Link';
import { AccountInfoContext } from '../../../account/account-info';

import { TicketUnaddressedToggle } from '../../TicketUnaddressedToggle';
import { CopyButton } from './CopyButton';

declare global {
  interface Window {
    sf_lightning_channel: {
      canConnect: () => boolean;
      sendWSMessage: (data:any) => string;
    }
  }
}

export interface TicketContactProps {
  ticket: string,
  handleDeviceBinPress: () => void,
}

export const TicketContact = ({ ticket, handleDeviceBinPress }: TicketContactProps) => {
  const contactData = useSelector(_.partial(ticketContactDataSelector, ticket));
  const channel = useSelector(_.partial(ticketChannelSelector, ticket));
  const salesforceObjId = useSelector(_.partial(ticketSalesforceObjectIdSelector, ticket));
  const salesforceObjType = useSelector(_.partial(ticketSalesforceObjectTypeSelector, ticket));
  const salesforcePayload = { 'record_id': salesforceObjId, 'sobject_type': salesforceObjType, 'event_type': 'navigate_to_record' };
  const phone = contactData?.phone && new PhoneNumber(contactData.phone);
  const { salesforceInstance } = useContext(AccountInfoContext);

  const { device_type }:any = useSelector<any>((state) => state.ticket.data[ticket]) || {};

  const copyButtonRef = useRef<CopyButton>();
  const handlePressPhone = useCallback(() => {
    copyButtonRef.current.trigger('copy-phone-domestic-target');
  }, [copyButtonRef]);

  const salesforceLink = salesforceInstance && salesforceObjType && salesforceObjId ? `${salesforceInstance}/lightning/r/${salesforceObjType}/${salesforceObjId}/view` : null;
  const channelOnClick = () => window.sf_lightning_channel.sendWSMessage(salesforcePayload);
  const useChannel = salesforceLink && window.sf_lightning_channel.canConnect();
  const linkTitle = useChannel ? `Display ${salesforceObjType}` : `Link to Salesforce ${salesforceObjType}`
  const deviceRevealerClassName = `device-${device_type || 'unknown'} copy-button revealer noselect`;
  return (
    <div className="ticket-contact">
      <div id="ticket-addressed-button" className="noselect">
        <TicketUnaddressedToggle ticket={ticket} />
      </div>
      <div className="title">
        <div className="contact-info">
          {(contactData?.display_name && contactData?.display_name !== contactData?.phone
            ? (
              <>
                <div className="contact-name">
                  <AccountLink label={contactData.display_name} path={`contacts/${contactData.id}`} />
                </div>
                {channel === 'live_chat'
                  ? (
                    <div
                      className="contact-origin revealer noselect"
                      // target="#device-bin"
                    >
                      Live Chat
                    </div>
                  ) : (
                    <div className="contact-phone" id="contact-phone-copy">
                      <span
                        id="copy-phone-target"
                        onClick={handlePressPhone}
                        onKeyPress={handlePressPhone}
                        role="button"
                        tabIndex={0}
                      >
                        {phone ? `+${phone.getCountryCode()} ${phone.getNumber('national')}` : ''}
                      </span>
                      <span id="copy-phone-domestic-target" style={{ position: 'absolute', top: '-1000px', left: '-1000px' }}>
                        {phone ? phone.getNumber('national') : ''}
                      </span>
                    </div>
                  )}
              </>
            )
            : (
              <>
                <div className="contact-name" id="contact-phone-copy">
                  {phone && (
                    <AccountLink path={`contacts/${contactData.id}`}>
                      <span id="copy-phone-target">
                        {`+${phone.getCountryCode()} ${phone.getNumber('national')}`}
                      </span>
                    </AccountLink>
                  )}
                  {!phone && contactData && <span id="copy-phone-target">Restricted</span>}
                </div>
                <div className="contact-phone" />
              </>
            )
          )}
        </div>
      </div>
      {(channel === 'live_chat'
        ? (
          <span
            id="device-revealer"
            className={deviceRevealerClassName}
            onClick={handleDeviceBinPress}
          />
        )
        : (
          <CopyButton
            ref={copyButtonRef}
            target="copy-phone-target"
            id="contact-phone-copy-helper"
          />
        )
      )}
      {useChannel && (
        <Link
          path="#"
          handlePress={channelOnClick}
        >
         <span id="ticket-sf-case-link" title={linkTitle}></span>
        </Link>
      )}
      {!useChannel && salesforceLink && (
        <a
          id="ticket-sf-case-link"
          title={linkTitle}
          target="_blank"
          rel="noreferrer"
          href={salesforceLink}
        >
          
        </a>
      )}
    </div>
  );
};
